// Auto SKU Generator Utility
// Format: name-number (e.g., cotton-shirt-00050)

// Generate SEO-friendly URL from product name
export const generateSEOUrlFromName = (productName) => {
  if (!productName || productName.trim() === '') {
    return 'unnamed-product';
  }
  
  return productName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

// Generate Product ID from SKU
export const generateProductIdFromSKU = (sku) => {
  if (!sku) return '';
  
  // Convert SKU to uppercase and replace hyphens with underscores
  return sku.toUpperCase().replace(/-/g, '_');
};

// Generate auto-incrementing number starting from 00050
const generateSequenceNumber = (existingProducts = []) => {
  // Extract all SKUs and find the highest sequence number
  let maxSequence = 49; // Start from 49 so next will be 50
  
  existingProducts.forEach(product => {
    if (product.sku) {
      // Extract sequence number from SKU (last 5 digits)
      const skuParts = product.sku.split('-');
      if (skuParts.length >= 2) {
        const sequencePart = skuParts[skuParts.length - 1];
        const sequenceNumber = parseInt(sequencePart);
        if (!isNaN(sequenceNumber) && sequenceNumber > maxSequence) {
          maxSequence = sequenceNumber;
        }
      }
    }
  });
  
  // Return next sequence number padded to 5 digits
  return String(maxSequence + 1).padStart(5, '0');
};

// Main SKU generation function - now based on product name
export const generateAutoSKU = (productName, existingProducts = []) => {
  try {
    if (!productName || productName.trim() === '') {
      // Fallback SKU if no name provided
      const fallbackSequence = generateSequenceNumber(existingProducts);
      return `unnamed-product-${fallbackSequence}`;
    }
    
    // Clean the product name for SKU
    const cleanName = productName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    const sequenceNumber = generateSequenceNumber(existingProducts);
    const sku = `${cleanName}-${sequenceNumber}`;
    
    console.log('🔢 Generated SKU:', {
      productName,
      cleanName,
      sequenceNumber,
      finalSKU: sku
    });
    
    return sku;
  } catch (error) {
    console.error('Error generating SKU:', error);
    // Fallback SKU
    const fallbackSequence = generateSequenceNumber(existingProducts);
    return `product-${fallbackSequence}`;
  }
};

// Generate SKU preview (without sequence number for display)
export const generateSKUPreview = (productName) => {
  if (!productName || productName.trim() === '') {
    return 'product-name-XXXXX';
  }
  
  const cleanName = productName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  return `${cleanName}-XXXXX`;
};

// Validate SKU format
export const validateSKUFormat = (sku) => {
  if (!sku) return false;
  
  // Pattern: name-number (e.g., cotton-shirt-00050)
  const skuPattern = /^[a-z0-9-]+-\d{5}$/;
  return skuPattern.test(sku);
};

export default {
  generateAutoSKU,
  generateSKUPreview,
  generateSEOUrlFromName,
  generateProductIdFromSKU,
  validateSKUFormat,
  generateSequenceNumber
};
